import { ReactNode, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import Image from "next/image";

import Button from "../common/Button";
import CircleBadge from "../common/CircleBadge";
import Icon from "../common/Icon";

import { ButtonVariants } from "../common/Button/constants";
import * as styledElements from "./HomePageStyles";
import { COMPONENTS, CONTACT, DOCS } from "../../constants/routes";
import Hero from "./Hero";
import { CADSources, uploadBoardForCad } from "../../reducers/uploadedBoard";
import { HideIfAboveWidth, ShowIfAboveWidth } from "../../constants/Styles";
import { HAMBURGER_PX } from "../Header/HeaderStyles";
import { FlashPCBSettings } from "../../utils/settings";

const THREE_STEP_HEADER_TEXT = "The prototyping solution in 3 simple steps:";

export interface HomepageProps {
  title: string;
  description: string;
  flashPCBSettings: FlashPCBSettings;
  defaultCadUpload?: CADSources;
  middleMaterials?: ReactNode;
}

const ThreeStepText = ({
  index,
  selectedIndex,
  alt,
  text,
  contentLength,
}: {
  index: number;
  selectedIndex: number;
  alt: string;
  text: string;
  contentLength: number;
}) => {
  const didScrollPast = index <= selectedIndex;
  return (
    <styledElements.MobileStepContainer key={alt}>
      <CircleBadge
        text={`${index + 1}`}
        type={didScrollPast ? ButtonVariants.PRIMARY : ButtonVariants.SECONDARY}
        hasBottomConnector={index < contentLength - 1}
        bottomConnectorVariant={
          index + 1 <= selectedIndex ? "primary" : "secondary"
        }
        isLink={false}
      />
      {text}
    </styledElements.MobileStepContainer>
  );
};

const DocsButton = ({ label, route }: { label: string; route: string }) => {
  const router = useRouter();
  return (
    <Button
      leftIconProps={{
        src: "/icons/tag.svg",
        alt: "get-started",
      }}
      label={label}
      variant={ButtonVariants.SECONDARY}
      onClick={() => void router.push(route)}
      centerText
      isSmallSize
    />
  );
};

const HomePage = ({
  title,
  description,
  flashPCBSettings,
  defaultCadUpload,
  middleMaterials,
}: HomepageProps) => {
  const threeStepContent = [
    {
      text: "Upload your CAD Files",
      description:
        "Our design rule checks ensure that your board is manufacturable which means fewer issues that could slow down production.",
      image: "upload_gray.svg",
      mobileImage: "upload.svg",
      buttonVariant: ButtonVariants.SECONDARY,
      buttonText: "Upload your design",
      buttonRoute: uploadBoardForCad(defaultCadUpload),
    },
    {
      text: "Review your BOM",
      description:
        "We help you select in stock components and verify your footprints to ensure we can assemble them.",
      image: "boards_gray.svg",
      mobileImage: "boards.svg",
      buttonVariant: ButtonVariants.SECONDARY,
      buttonText: "Browse Parts",
      buttonRoute: COMPONENTS,
      cardImageDimension: 125,
    },
    {
      text: "Order your Board",
      description:
        "See your quote, order your board, and rest easy that we've got it from here.",
      image: "wrench_gray.svg",
      mobileImage: "wrench.svg",
      buttonVariant: ButtonVariants.PRIMARY,
      buttonText: "Order board",
      buttonRoute: uploadBoardForCad(defaultCadUpload),
    },
  ];
  const router = useRouter();
  const cardContainerRef = useRef<HTMLDivElement | null>(null);
  const lastCardRef = useRef<HTMLDivElement | null>(null);
  const [isSidebarSticky, setIsSidebarSticky] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [didScrollPastCardArea, setDidScrollPastCardArea] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (cardContainerRef.current && lastCardRef.current) {
        const topOfCardContainer =
          cardContainerRef.current.getBoundingClientRect().top;
        const topOfLastCard = lastCardRef.current.getBoundingClientRect().top;
        // sidebar is sticky when scrolling past card area
        setIsSidebarSticky(
          topOfCardContainer <= styledElements.STICKY_SIDEBAR_TOP_MARGIN &&
            topOfLastCard >= 0,
        );

        const cardMargin = styledElements.CARD_BOTTOM_MARGIN_NUMBER;
        const cardIndex = Math.max(
          0, // anything below zero is before the card container
          Math.min(
            Math.floor(
              (-topOfCardContainer + cardMargin) /
                (styledElements.CARD_HEIGHT_NUMBER + cardMargin),
            ),
            threeStepContent.length - 1, // anything after 2 is after the card container
          ),
        );
        setSelectedCardIndex(cardIndex);

        // prevents Sidebar from suddenly disappearing when scrolling past it
        setDidScrollPastCardArea(topOfLastCard < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [threeStepContent.length]);

  const mobileCards = (
    <styledElements.MobileCardsContainer>
      <styledElements.MobileHeaderText>
        {THREE_STEP_HEADER_TEXT}
      </styledElements.MobileHeaderText>
      {threeStepContent.map((step, index) => (
        <styledElements.MobileCardContainer key={index}>
          <styledElements.CardHeaderText>
            <CircleBadge text={`${index + 1}`} type={ButtonVariants.PRIMARY} />
            {step.text}
          </styledElements.CardHeaderText>
          <styledElements.MobileCard>
            <Icon
              src={`/icons/${step.mobileImage}`}
              alt={step.text}
              iconDimension={48}
            />
            <styledElements.MobileCardContent>
              {step.description}
            </styledElements.MobileCardContent>
            <styledElements.InvisibleLink href={step.buttonRoute}>
              <Button
                label={step.buttonText}
                variant={ButtonVariants.SECONDARY}
                rightIconProps={{
                  src: "/icons/expand_right.svg",
                  alt: `${step.text}`,
                  iconDimension: 16,
                }}
              />
            </styledElements.InvisibleLink>
          </styledElements.MobileCard>
        </styledElements.MobileCardContainer>
      ))}
    </styledElements.MobileCardsContainer>
  );

  const cards = (
    <styledElements.CardsContainer>
      <styledElements.SidebarContainer>
        <styledElements.Sidebar
          sticky={isSidebarSticky}
          didScrollPastCardArea={didScrollPastCardArea}
        >
          <styledElements.SubheaderText>
            {THREE_STEP_HEADER_TEXT}
          </styledElements.SubheaderText>
          {threeStepContent.map((step, index) => (
            <ThreeStepText
              key={step.text}
              index={index}
              selectedIndex={selectedCardIndex}
              alt={step.text}
              text={step.text}
              contentLength={threeStepContent.length}
            />
          ))}
        </styledElements.Sidebar>
      </styledElements.SidebarContainer>
      <div ref={cardContainerRef}>
        {threeStepContent.map((step, index) => (
          <styledElements.StepContainer
            key={step.text}
            ref={index == threeStepContent.length - 1 ? lastCardRef : null}
          >
            <styledElements.CardImageContainer>
              <Image
                src={`/icons/${step.mobileImage}`}
                alt={step.text}
                width={step.cardImageDimension || 200}
                height={step.cardImageDimension || 200}
              />
            </styledElements.CardImageContainer>
            <styledElements.TextArea>
              <styledElements.CardContent>
                {step.description}
              </styledElements.CardContent>
              <styledElements.CardButtonContainer>
                <styledElements.InvisibleLink href={step.buttonRoute}>
                  <Button
                    label={step.buttonText}
                    rightIconProps={styledElements.expandIconProps}
                    variant={step.buttonVariant}
                  />
                </styledElements.InvisibleLink>
              </styledElements.CardButtonContainer>
            </styledElements.TextArea>
          </styledElements.StepContainer>
        ))}
      </div>
    </styledElements.CardsContainer>
  );

  const getStartedAndNeedHelpCards = (
    <styledElements.HelpCardsContainer>
      <styledElements.GetStartedCard>
        <styledElements.TitleText>How to Get Started</styledElements.TitleText>
        <styledElements.ContentText>
          Read our docs to understand how to get your board made with FlashPCB
        </styledElements.ContentText>
        <Button
          label="Read our Docs"
          rightIconProps={styledElements.expandIconProps}
          onClick={() => void router.push(DOCS)}
        />
        <ShowIfAboveWidth width={HAMBURGER_PX}>
          <styledElements.DocsButtonContainer>
            <DocsButton
              label="Getting Started"
              route={`${DOCS}/getting-started`}
            />
            <DocsButton
              label="Designing Your Board"
              route={`${DOCS}/designing-your-board`}
            />
            <DocsButton
              label="Uploading Your Board"
              route={`${DOCS}/uploading-your-board`}
            />
          </styledElements.DocsButtonContainer>
        </ShowIfAboveWidth>
        <HideIfAboveWidth width={HAMBURGER_PX}>
          <styledElements.MobileDocsButtonContainer>
            <div>
              <DocsButton
                label="Getting Started"
                route={`${DOCS}/getting-started`}
              />
            </div>
            <div>
              <DocsButton
                label="Designing Your Board"
                route={`${DOCS}/designing-your-board`}
              />
            </div>
            <div>
              <DocsButton
                label="Uploading Your Board"
                route={`${DOCS}/uploading-your-board`}
              />
            </div>
          </styledElements.MobileDocsButtonContainer>
        </HideIfAboveWidth>
      </styledElements.GetStartedCard>
      <styledElements.NeedHelpCard>
        <styledElements.IconContainer>
          <Image
            src="/images/single_letter_logo.png"
            alt="flash-pcb-logo"
            width={80}
            height={80}
          />
          <Image
            src="/images/eclipse_group_circle.png"
            alt="eclipse"
            width={245}
            height={245}
          />
        </styledElements.IconContainer>
        <styledElements.ContactTextArea>
          <styledElements.TitleText>Need help?</styledElements.TitleText>
          <p>
            <span>Find us at</span>{" "}
            {flashPCBSettings.flashpcb_address.street_address},{" "}
            {flashPCBSettings.flashpcb_address.last_line} <span>or</span>
          </p>
        </styledElements.ContactTextArea>
        <Button
          label="Contact Us"
          onClick={() => void router.push(CONTACT)}
          leftIconProps={{
            src: "/icons/mail.svg",
            alt: "contact-us",
          }}
          centerText
        />
      </styledElements.NeedHelpCard>
    </styledElements.HelpCardsContainer>
  );

  return (
    <styledElements.Container>
      <Hero
        title={title}
        description={description}
        defaultCadUpload={defaultCadUpload}
      />
      {middleMaterials}
      <ShowIfAboveWidth width={HAMBURGER_PX}>{cards}</ShowIfAboveWidth>
      <HideIfAboveWidth width={HAMBURGER_PX}>{mobileCards}</HideIfAboveWidth>
      {getStartedAndNeedHelpCards}
    </styledElements.Container>
  );
};

export default HomePage;
